// ============================================================
// usePlateHits - what a plate plays of the attack that just struck it (plan
// body-map H1, H2). The shell's live impacts (engine/impacts) last a couple
// of seconds; a plate plays each NEW one once, from its arrival, as a burst
// (engine/plate/burst: glances first, then each hit in turn, the rattle) for
// as long as it takes - longer than the impact may live. The impact already
// there when the plate mounts is history and never plays (a tab switched
// mid-flash). An attack with nothing to show here (a clean miss, hits that
// land nowhere on this body) leaves a burst in progress alone.
// ============================================================

import { useContext, useEffect, useRef, useState } from 'react';
import { notePlatePlayed } from '../../diagnostics/plateLog';
import { PlateWatch } from './usePlateWatch';
import type { Impact } from '../../engine/impacts';
import { burstOf, burstSpan, type Burst } from '../../engine/plate/burst';
import { playedRules, specialMoments, specialsSpan, type SpecialMoment } from '../../engine/plate/specials';

export interface HitPlay {
  /** The impact's id: what keys each struck location's lines, so a new hit restarts its signature. */
  id: string;
  burst: Burst;
  /** The weapon's specials over the burst (engine/plate/specials), worked out once when it arrives. */
  moments: SpecialMoment[];
  /** How long the play lasts (s): the burst, its specials' looks and the body's own hold, fixed when it arrives. */
  span: number;
}

/**
 * `holdFor`: until when the plate's own body moves under a burst, when that outlasts it (the goo's mass, a look's
 * moment on its hits); the play is kept that long too. Read once, when the burst arrives.
 */
export default function usePlateHits(
  impact: Impact | null | undefined,
  locations: readonly string[] | null,
  holdFor?: (burst: Burst) => number,
) {
  const play = useNewPlay(impact, locations, holdFor);
  return { play, shownAt: useMoments(play), elapsed: usePlayClock(play), clocked: useClocked(play) };
}

const showsAnything = (b: Burst) => b.hits.length > 0 || b.glances.length > 0;

/** A burst's play: as long as the burst, its specials' looks and the body's own hold take (each can outlast it). */
function playOf(id: string, burst: Burst, holdFor: ((burst: Burst) => number) | undefined): HitPlay {
  const moments = specialMoments(playedRules(burst), burst.hits);
  const span = Math.max(burstSpan(burst), specialsSpan(moments), holdFor?.(burst) ?? 0);
  return { id, burst, moments, span };
}

/** The burst of each impact that arrives after mount, kept until its last moment is over. */
function useNewPlay(
  impact: Impact | null | undefined,
  locations: readonly string[] | null,
  holdFor: ((burst: Burst) => number) | undefined,
): HitPlay | null {
  const [seen, setSeen] = useState(impact?.id ?? null);
  const [play, setPlay] = useState<HitPlay | null>(null);
  // A new impact arrived (state adjusted during render, as useArrived does). One with no hits and no glances (a
  // body action, plan design-gaps K2) shows nothing, so plays nothing.
  if (impact && impact.id !== seen) {
    setSeen(impact.id);
    const burst = burstOf(impact, locations);
    if (showsAnything(burst)) setPlay(playOf(impact.id, burst, holdFor));
  }
  const watch = useContext(PlateWatch);
  useEffect(() => {
    if (!play) return undefined;
    if (watch) notePlatePlayed(watch, play.id, play);
    const done = setTimeout(() => setPlay(null), play.span * 1000);
    return () => clearTimeout(done);
  }, [play, watch]);
  return play;
}

/**
 * The latest moment of the burst begun (seconds from its start): each hit in turn re-renders its location, so a
 * place struck twice plays twice. Timers, not the clock: with motion off the hits still land in turn.
 */
function useMoments(play: HitPlay | null): number {
  const [at, setAt] = useState<{ id: string; at: number } | null>(null);
  useEffect(() => {
    if (!play) return undefined;
    const moments = [...new Set(play.burst.hits.map((h) => h.at))].filter((m) => m > 0);
    const timers = moments.map((m) => setTimeout(() => setAt({ id: play.id, at: m }), m * 1000));
    return () => timers.forEach(clearTimeout);
  }, [play]);
  return play && at?.id === play.id ? at.at : 0;
}

/**
 * Whether the burst still needs the plate's clock: its filters, throw, strobe and wave are over at its span, while the
 * play may last on for its specials' looks (CSS, no clock).
 */
function useClocked(play: HitPlay | null): boolean {
  const [over, setOver] = useState<string | null>(null);
  useEffect(() => {
    if (!play) return undefined;
    const done = setTimeout(() => setOver(play.id), burstSpan(play.burst) * 1000);
    return () => clearTimeout(done);
  }, [play]);
  return play !== null && over !== play.id;
}

/**
 * Seconds into the burst at clock time `t`, counted from its first frame (the clock's own seconds, which a render
 * cannot read).
 */
function usePlayClock(play: HitPlay | null) {
  const start = useRef<{ id: string; t: number } | null>(null);
  return (t: number) => {
    if (!play) return 0;
    if (start.current?.id !== play.id) start.current = { id: play.id, t };
    return t - start.current.t;
  };
}
